import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  useTheme,
  useMediaQuery,
  Typography,
  Avatar,
  Button,
  TextField,
  Select,
  FormControl,
  FormLabel,
  FormControlLabel,
  CircularProgress,
  SelectChangeEvent,
  Chip,
  InputLabel,
  MenuItem,
  TableSortLabel,
  Skeleton,
} from "@mui/material";
import {
  FaEye,
  FaFileExcel,
  FaPencil,
  FaSpinner,
  FaXmark,
  FaFilter,
} from "react-icons/fa6";
import Breadcrumb from "../../../Components/BreadCrumb";
import RefreshButton from "../../../Components/RefreshButton";
import ChipStatus from "../../../Components/ChipStatus";
import {
  useExportCustomersMutation,
  useLazyGetCustomersQuery,
  useUpdateCustomerMutation,
  useUpdateCustomerStatusMutation,
} from "../../../Services/CustomerService";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import CsvDownload from "react-json-to-csv";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import { useDispatch, useSelector } from "react-redux";
import { showSnackbar } from "../../../Redux/Slices/NotificationSlice";
import ApiPath from "../../../Services/ConstantApi";
import {
  CustomerStatusOption,
  chipStyle,
  commonSelectStyle,
} from "../../../Helpers/Constant/CategoryContant";
import Utils from "../../../Helpers/Utils";
import { Dayjs } from "dayjs";
import CustomerSlidingFilter from "../../../Components/SlidingFilter/CustomerSlidingFilter";
import AppliedFilter from "../../../Components/AppliedFilter";

dayjs.extend(isBetween);

interface FiltersWithPagination {
  status?: string;
  page: number;
  rowsPerPage: number;
  sortBy: string;
  sortOrder: string;
  startDate?: string;
  endDate?: string;
}

// Filters for Customer Sliding Filter and AppliedFilter chips
interface CustomerFilters {
  startDate?: string; // YYYY-MM-DD
  endDate?: string; // YYYY-MM-DD
  status: string;
  search: string;
}

const Customers: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Reload without losing the filters, page and sort already set up.
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<any>(null);
  const handleRefresh = () => {
    setRefreshing(true);
    setRefreshKey((k) => k + 1);
  };
  const [filterOpen, setFilterOpen] = useState(false);
  const [page, setPage] = useState(0);
  const today = dayjs().format("YYYY-MM-DD");
  // const [page, setPage] = useState(0);
  // const [rowsPerPage, setRowsPerPage] = useState(10);
  const [open, setOpen] = React.useState(false);
  const [trigger, { data: customerData, reset, error, isLoading, isFetching }] =
    useLazyGetCustomersQuery();
  const [updateCustomerStatusTrigger, { isLoading: updateCustomerLoading }] =
    useUpdateCustomerStatusMutation();

  const [tempValue, setTempValue] = React.useState<any>({ status: "active" });
  const [loading, setLoading] = React.useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const useInfo = useSelector((state: any) => state.AuthSlice.adminAuthUser);

  const [exportCustomers, { isLoading: exportLoading }] =
    useExportCustomersMutation();

  const [filtersWithPagination, setFiltersWithPagination] =
    useState<FiltersWithPagination>({
      page: 0,
      rowsPerPage: 10,
      sortBy: "createdAt",
      sortOrder: "desc",
      status: "All",
      startDate: today,
      endDate: today,
    });

  const handleSort = (column: string) => {
    setFiltersWithPagination((prev) => {
      const isAsc = prev.sortBy === column && prev.sortOrder === "asc";
      return {
        ...prev,
        sortBy: column,
        sortOrder: isAsc ? "desc" : "asc",
        page: 0, // reset to first page on sort
      };
    });
  };

  useEffect(() => {
    const filtersArray = [
      ...Object.entries(filtersWithPagination)
        .filter(
          ([key]) =>
            key !== "page" &&
            key !== "rowsPerPage" &&
            key !== "sortBy" &&
            key !== "sortOrder"
        )
        .flatMap(([key, value]) => {
          // Skip "status" if it's "All"
          if (key === "status" && value === "All") return [];

          return {
            key,
            value:
              key === "status" && typeof value === "string"
                ? value.toLowerCase()
                : value,
          };
        }),
      { key: "isDelete", value: false },
    ];

    trigger({
      filters: filtersArray.filter((item: any) => item.value !== ""),
      search: debouncedSearch,
      sortBy: filtersWithPagination.sortBy || "createdAt",
      sortOrder: filtersWithPagination.sortOrder || "desc",
      page: filtersWithPagination.page + 1,
      pageSize: filtersWithPagination.rowsPerPage,
    }).finally(() => {
      setRefreshing(false);
      setLastUpdated(dayjs());
    });
  }, [filtersWithPagination, debouncedSearch, refreshKey]);

  const handleChangePage = (event: unknown, newPage: number) => {
    reset();
    setFiltersWithPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    reset();
    setFiltersWithPagination((prev) => ({
      ...prev,
      rowsPerPage: parseInt(event.target.value, 10),
    }));
  };

  const breadcrumbItems = [
    // { label: "Dashboard", path: "dashboard" },
    { label: "Customer List" },
  ];

  const handleEdit = (item: any) => {
    // navigate(`customers/edit/${id}`);
    setOpen(true);
    setTempValue({ ...item, status: item.status });
  };

  const handleChangeStatus = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTempValue({ ...tempValue, status: event.target.value });
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  /*
   * Why a customer has no name and no phone.
   *
   * Sign in with Google or Apple gives us an email and, if the person
   * agrees, a name. Neither ever gives a phone number, and Apple offers
   * the name only on the very first authorisation -- so four accounts
   * here have no name at all and twelve have no phone. Nothing is
   * missing; we were never told.
   *
   * A blank cell cannot say that, and "N/A" reads like an error code, so
   * both were going to be reported as bugs. The provider is shown beside
   * the row instead: the reason sits next to the gap.
   */
  const signInProvider = (item: any): string | null => {
    if (item?.loginMethod !== "social") return null;
    const p = item?.socialLogins?.[0]?.provider;
    if (!p) return "Social";
    return String(p).charAt(0).toUpperCase() + String(p).slice(1);
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format("DD MMM YYYY");
  };

  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [dateRange, setDateRange] = useState<[any, any] | null>([today, today]);
  const [filteredData, setFilteredData] = useState<any[]>([]);

  // Add these functions
  const filterData = useCallback(() => {
    // let filtered = [...customers];
    // if (selectedStatus !== 'all') {
    //   filtered = filtered.filter(customer => customer.status === selectedStatus);
    // }
    // if (dateRange) {
    //   const [startDate, endDate] = dateRange;
    //   // Ensure startDate and endDate are parsed as Day.js objects
    //   const start = dayjs(startDate);
    //   const end = dayjs(endDate);
    //   filtered = filtered.filter(customer => {
    //     const customerDate = dayjs(customer.createdAt);
    //     // Check if the customer's date is within the range (inclusive)
    //     return customerDate.isBetween(start, end, 'day', '[]');
    //   });
    // }
    // setFilteredData(filtered);
  }, [selectedStatus, dateRange]);

  const [filters, setFilters] = React.useState<CustomerFilters>({
    startDate: today,
    endDate: today,
    status: "all",
    search: "",
  });

  const [appliedFilters, setAppliedFilters] = useState<CustomerFilters>({
    startDate: today,
    endDate: today,
    status: "all",
    search: "",
  });

  const getExportData = () => {
    return filteredData.map((customer) => ({
      "Customer ID": customer.id,
      Name: customer.name,
      Email: customer.email,
      Phone: customer.phone,
      Status: customer.status,
      "Created Date": dayjs(customer.createdAt).format("YYYY-MM-DD"),
    }));
  };

  const handleSearchInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    reset();
    setSearchTerm(event.target.value);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setFiltersWithPagination((prev) => ({ ...prev, page: 0 })); // Reset to first page on new search
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    filterData();
  }, [filterData]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = async () => {
    setLoading(true);
    const response = await updateCustomerStatusTrigger({
      customerId: tempValue._id,
      status: tempValue.status,
    });
    if (response && response.data && response.data.status === "success") {
      dispatch(
        showSnackbar({
          message: "Customer Status updated successfully",
          severity: "success",
        })
      );
      loaderClose();
    } else {
      dispatch(
        showSnackbar({
          message: "Failed to update customer",
          severity: "error",
        })
      );
      loaderClose();
    }
  };

  const loaderClose = () => {
    setTimeout(() => {
      setOpen(false);
      setLoading(false);
    }, 1500);
  };

  const handleExport = async () => {
    const filtersArray = [
      ...Object.entries(filtersWithPagination)
        .filter(
          ([key]) =>
            key !== "page" &&
            key !== "rowsPerPage" &&
            key !== "sortBy" &&
            key !== "sortOrder"
        )
        .map(([key, value]) => ({
          key,
          value:
            key === "status" && typeof value === "string"
              ? value.toLowerCase()
              : value,
        })),
      { key: "isDelete", value: false },
    ];

    try {
      const blob: any = await exportCustomers({
        search: searchTerm.trim(),
        filters: filtersArray.filter((item: any) => item.value !== ""),
      }).unwrap();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "customers.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      dispatch(
        showSnackbar({
          message: "No customers found for export",
          severity: "error",
        })
      );
      // Optionally show error to user
    }
  };

  const handleChange =
    (key: "status") => (event: SelectChangeEvent<string>) => {
      reset();
      setFiltersWithPagination((prev) => ({
        ...prev,
        [key]: event.target.value,
        page: 0,
      }));
    };

  const handleReset = () => {
    reset();
    setSearchTerm("");
    setFiltersWithPagination((prev) => ({ ...prev, status: "All", page: 0 }));
  };

  const handleFilterChange = (filterName: string, value: string | boolean) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  const handleApplyFilters = (filtersToApply: CustomerFilters) => {
    setAppliedFilters(filtersToApply);

    // Update the main filters with pagination
    setFiltersWithPagination((prev) => {
      const updatedFilters: any = {
        ...prev,
        page: 0, // Reset to first page when applying filters
        status: filtersToApply.status === "all" ? "All" : filtersToApply.status,
      };

      // Set/clear independent dates
      updatedFilters.startDate = filtersToApply.startDate || "";
      updatedFilters.endDate = filtersToApply.endDate || "";

      return updatedFilters;
    });

    // Update search term if provided (let debounce effect handle debouncedSearch)
    if (filtersToApply.search) {
      setSearchTerm(filtersToApply.search);
    } else {
      setSearchTerm("");
    }
    setFilterOpen(false); // Close the filter panel
  };

  const handleClearSingleFilter = (filterKey: string) => {
    // Update applied chips state
    setAppliedFilters((prev) => {
      const updated = { ...prev } as CustomerFilters;
      if (filterKey === "status") updated.status = "all";
      else if (filterKey === "startDate") updated.startDate = "";
      else if (filterKey === "endDate") updated.endDate = "";
      else if (filterKey === "search") updated.search = "";
      return updated;
    });

    // Update sliding filter panel state
    setFilters((prev) => {
      const updated = { ...prev } as CustomerFilters;
      if (filterKey === "status") updated.status = "all";
      else if (filterKey === "startDate") updated.startDate = "";
      else if (filterKey === "endDate") updated.endDate = "";
      else if (filterKey === "search") updated.search = "";
      return updated;
    });

    // Update main query filters with pagination
    setFiltersWithPagination((prev) => {
      const updated: FiltersWithPagination = { ...prev, page: 0 };
      if (filterKey === "status") updated.status = "All";
      if (filterKey === "startDate") updated.startDate = "";
      if (filterKey === "endDate") updated.endDate = "";
      // Note: search term is handled via setSearchTerm for debouncing
      return updated;
    });

    // Clear debounced search input flow
    if (filterKey === "search") {
      setSearchTerm("");
    }
  };

  const handleClearFilters = () => {
    const clearedFilters: CustomerFilters = {
      startDate: "",
      endDate: "",
      status: "all",
      search: "",
    };
    setFilters(clearedFilters);
    setAppliedFilters(clearedFilters);

    // Clear the main filters with pagination and remove date filters
    setFiltersWithPagination((prev) => {
      return {
        ...prev,
        page: 0,
        status: "All",
        startDate: "",
        endDate: "",
      };
    });

    // Clear search term (let debounce effect handle debouncedSearch)
    setSearchTerm("");

    // setFilterOpen(false); // Close the filter panel
  };

  // Derived filters object for AppliedFilter chips. Convert defaults to empty so chips hide them
  const appliedFiltersForChips = {
    status:
      appliedFilters.status === "all"
        ? ""
        : appliedFilters.status === "active"
        ? "Active"
        : appliedFilters.status === "inactive"
        ? "Inactive"
        : appliedFilters.status === "ban"
        ? "Ban"
        : "",
    search: appliedFilters.search,
    startDate: appliedFilters.startDate || "",
    endDate: appliedFilters.endDate || "",
  };

  const activeFiltersCount = Object.values(appliedFiltersForChips).filter(
    (v) => v !== "" && v !== null && v !== undefined
  ).length;
  const isNarrow = useMediaQuery(theme.breakpoints.down("lg"));
  const hideFilterText = isNarrow && activeFiltersCount > 1;

  // Formatters for independent start/end date chips
  const chipFormatters = {
    startDate: (value: string) =>
      value ? dayjs(value).format("YYYY-MM-DD") : "",
    endDate: (value: string) =>
      value ? dayjs(value).format("YYYY-MM-DD") : "",
  } as const;

  return (
    <Box>
      <div
        style={{
          flexGrow: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Breadcrumb items={breadcrumbItems} name={"Customers"} />
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <AppliedFilter
            filters={appliedFiltersForChips}
            onClearFilter={handleClearSingleFilter}
            onClearAll={handleClearFilters}
            filterLabels={{
              startDate: "Start Date",
              endDate: "End Date",
              status: "Status",
              search: "Search",
            }}
            customFormatters={chipFormatters}
          />
          <RefreshButton onRefresh={handleRefresh} refreshing={refreshing} lastUpdated={lastUpdated} />
          <Button
            color="secondary"
            size={"medium"}
            sx={{
              height: 34,
              textTransform: "capitalize",
              boxShadow: theme.shadows[1],
            }}
            variant="outlined"
            startIcon={<FaFilter size={16} />}
            onClick={() => setFilterOpen(true)}
          >
            {!hideFilterText && (
              <Box component="span" sx={{ ml: 1 }}>
                Apply Filters
              </Box>
            )}
          </Button>
          {/* Status Filter */}
          {/* <FormControl sx={{ width: 100 }} size="small">
            <InputLabel
              id="status-label"
              sx={{
                fontSize: "12px",
                top: "2px",
                backgroundColor: "transparent",
              }}
            >
              Status
            </InputLabel>
            <Select
              value={filtersWithPagination.status}
              onChange={handleChange("status")}
              labelId="status-label"
              label="Status"
              sx={{ ...commonSelectStyle, width: 100, backgroundColor: "#fff" }}
            >
              <MenuItem value="All">All</MenuItem>

              {CustomerStatusOption.map((category: any) => (
                <MenuItem key={category.value} value={category.value}>
                  {category.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <section id="search-section">
            <TextField
              label="Search Customer"
              placeholder="Search Customer"
              variant="outlined"
              type="search"
              size="small"
              value={searchTerm}
              onChange={handleSearchInput}
              sx={{ ...Utils.commonSearchStyle() }}
              InputProps={{ ...Utils.commonInputPropsSearchStyle() }}
            />
          </section>

          {searchTerm != "" || filtersWithPagination?.status !== "All" ? (
            <div style={{ marginRight: 8, marginTop: 2 }}>
              <Button variant="outlined" color="warning" onClick={handleReset}>
                Reset
              </Button>
            </div>
          ) : (
            <div style={{ marginRight: 8, marginTop: 2 }} />
          )} */}

          {useInfo?.permissions?.customers?.edit && (
            <Button
              variant="outlined"
              color={"warning"}
              sx={{ mb: 2 }}
              style={{ marginTop: "15px" }}
              onClick={handleExport}
            >
              {exportLoading ? (
                <FaSpinner className="animate-spin" />
              ) : (
                <FaFileExcel />
              )}
              &nbsp;&nbsp;Export
            </Button>
          )}
        </div>
      </div>

      <Paper
        sx={{
          mt: 2,
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[1],
        }}
      >
        <TableContainer
          sx={{
            maxHeight: "calc(100vh - 220px)", // Adjust based on your layout
            overflow: "auto",
            "& .MuiTable-root": {
              minWidth: 1000, // Set minimum width to enable horizontal scrolling
            },
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              "& .MuiTableHead-root": {
                "& .MuiTableCell-root": {
                  backgroundColor: theme.palette.background.paper,
                  fontWeight: 600,
                  borderBottom: `2px solid ${theme.palette.divider}`,
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                },
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", minWidth: 60 }}>
                  #
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: "bold",
                    minWidth: 200,
                    "& .MuiTableSortLabel-icon": {
                      opacity: "1!important",
                    },
                  }}
                  sortDirection={
                    filtersWithPagination.sortBy === "firstName"
                      ? (filtersWithPagination.sortOrder as "asc" | "desc")
                      : false
                  }
                >
                  <TableSortLabel
                    active={filtersWithPagination.sortBy === "firstName"}
                    direction={
                      filtersWithPagination.sortBy === "firstName"
                        ? (filtersWithPagination.sortOrder as "asc" | "desc")
                        : "asc"
                    }
                    onClick={() => handleSort("firstName")}
                  >
                    Customer
                  </TableSortLabel>
                </TableCell>

                <TableCell
                  sx={{
                    fontWeight: "bold",
                    minWidth: 200,
                    "& .MuiTableSortLabel-icon": {
                      opacity: "1!important",
                    },
                  }}
                  sortDirection={
                    filtersWithPagination.sortBy === "email"
                      ? (filtersWithPagination.sortOrder as "asc" | "desc")
                      : false
                  }
                >
                  <TableSortLabel
                    active={filtersWithPagination.sortBy === "email"}
                    direction={
                      filtersWithPagination.sortBy === "email"
                        ? (filtersWithPagination.sortOrder as "asc" | "desc")
                        : "asc"
                    }
                    onClick={() => handleSort("email")}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>

                <TableCell sx={{ fontWeight: "bold", minWidth: 140 }}>
                  Phone
                </TableCell>
                {/*
                  What each customer has bought, and spent.
                  
                  These were built as a separate "Customer Reports" screen
                  that could never be reached: two routes were registered on
                  the same path and this tab always matched first, so that
                  screen still held a hardcoded "John Doe" row. Its four
                  figures were the only thing it added over this table, so
                  they live here instead.
                  
                  Only PAID orders count, and an order is counted once
                  however many items it held -- a basket of five gift cards
                  is one order, not five.
                */}
                <TableCell sx={{ fontWeight: "bold", minWidth: 110 }} align="center">
                  Total Orders
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 110 }} align="right">
                  Total Spent
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 150 }}>
                  Last Order
                </TableCell>
                {/* <TableCell sx={{ fontWeight: 'bold' }}>Address</TableCell> */}
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    minWidth: 180,
                    "& .MuiTableSortLabel-icon": {
                      opacity: "1!important",
                    },
                  }}
                  sortDirection={
                    filtersWithPagination.sortBy === "createdAt"
                      ? (filtersWithPagination.sortOrder as "asc" | "desc")
                      : false
                  }
                >
                  <TableSortLabel
                    active={filtersWithPagination.sortBy === "createdAt"}
                    direction={
                      filtersWithPagination.sortBy === "createdAt"
                        ? (filtersWithPagination.sortOrder as "asc" | "desc")
                        : "asc"
                    }
                    onClick={() => handleSort("createdAt")}
                  >
                    Joined Date
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 120 }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 100 }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading || isFetching
                ? [...Array(10)].map((_, index) => (
                    <TableRow key={index}>
                      {[...Array(10)].map((_, colIndex) => (
                        <TableCell key={colIndex}>
                          {colIndex === 7 ? (
                            <IconButton size="small" disabled>
                              <FaEye size={16} />
                            </IconButton>
                          ) : (
                            <Skeleton
                              variant="text"
                              width={colIndex === 0 ? 30 : "80%"}
                            />
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : null}

              {!isLoading &&
                !isFetching &&
                customerData?.data &&
                Array.isArray(customerData.data) &&
                customerData.data.map((item: any, index: any) => (
                  <TableRow
                    key={item._id}
                    sx={{
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <TableCell>
                      {filtersWithPagination.page *
                        filtersWithPagination.rowsPerPage +
                        index +
                        1}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Avatar
                          sx={{
                            width: 35,
                            height: 35,
                            bgcolor: theme.palette.primary.main,
                            fontSize: "14px",
                          }}
                        >
                          {getInitials(
                            `${item.firstName || ""} ${item.lastName || ""}`.trim(),
                          )}
                        </Avatar>
                        <Box>
                          <Typography variant="body2">
                            {`${item.firstName || ""} ${item.lastName || ""}`.trim() ||
                              "—"}
                          </Typography>
                          {signInProvider(item) ? (
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary" }}
                            >
                              {signInProvider(item)} sign-in
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{item.email || "—"}</TableCell>
                    {/* A dash, not "N/A": the same mark the other columns
                        use for "nothing here", and not an error code. */}
                    <TableCell>{item.phoneNumber || "—"}</TableCell>
                    <TableCell align="center">{item.totalOrders ?? 0}</TableCell>
                    <TableCell align="right">
                      {Utils.formatEuro(Number(item.totalSpent || 0).toFixed(2))}
                    </TableCell>
                    <TableCell>
                      {/* A customer who has never bought gets a dash, not a
                          date that would have to be invented. */}
                      {item.lastOrderDate
                        ? formatDate(item.lastOrderDate)
                        : "—"}
                    </TableCell>
                    {/* <TableCell>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      maxWidth: 200,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {item.addresses}
                  </Typography>
                </TableCell> */}
                    {/* <TableCell>{formatDate(item.createdAt)}</TableCell> */}
                    <TableCell>
                      {dayjs(item.createdAt).format("DD.MM.YYYY, HH:mm:ss")}
                    </TableCell>

                    <TableCell>
                      <ChipStatus
                        status={
                          item?.status === "active"
                            ? "active"
                            : item?.status === "ban"
                            ? "blocked"
                            : "warning"
                        }
                        label={
                          item?.status === "active"
                            ? "Active"
                            : item?.status === "ban"
                            ? "Banned"
                            : "Inactive"
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {useInfo?.permissions?.customers?.edit && (
                        <IconButton
                          onClick={() => handleEdit(item)}
                          size="small"
                          sx={{ color: theme.palette.warning.main, ml: 1 }}
                        >
                          <FaPencil size={14} />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        sx={{ color: theme.palette.primary.main }}
                        onClick={() =>
                          navigate(`/customers/detail/${item._id}`)
                        }
                      >
                        <FaEye size={16} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}

              {!isLoading &&
                ((error as any)?.data?.message ||
                  customerData?.data?.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 1 }}>
                      {(error as any)?.data?.message || "No Records Found."}
                    </TableCell>
                  </TableRow>
                )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          showFirstButton
          showLastButton
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={customerData?.totalRecords ?? 0}
          rowsPerPage={filtersWithPagination.rowsPerPage}
          page={filtersWithPagination.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        />
      </Paper>

      <CustomerSlidingFilter
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        filters={filters}
        onFilterChange={handleFilterChange}
      />

      <Dialog
        sx={{ "& .MuiDialog-paper": { width: "80%", maxHeight: 435 } }}
        maxWidth="xs"
        // TransitionProps=÷{{ onEntering: handleEntering }}
        open={open}
        onClose={handleClose}
      >
        <DialogTitle>Update Customer Status</DialogTitle>
        <IconButton
          aria-label="close"
          size="small"
          onClick={handleClose}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <FaXmark />
        </IconButton>
        <DialogContent dividers>
          <FormControl>
            <FormLabel id="demo-radio-buttons-group-label">
              Customer Status
            </FormLabel>
            <RadioGroup
              aria-labelledby="demo-radio-buttons-group-label"
              defaultValue="active"
              name="radio-buttons-group"
              value={tempValue.status}
              onChange={handleChangeStatus}
            >
              <FormControlLabel
                value="active"
                control={<Radio />}
                label="Active"
              />
              <FormControlLabel
                value="inactive"
                control={<Radio />}
                label="Inactive"
              />
              <FormControlLabel value="ban" control={<Radio />} label="Ban" />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          {updateCustomerLoading || isLoading || loading ? (
            <CircularProgress sx={{ mr: 1 }} color="primary" size={24} />
          ) : null}
          <Button
            disabled={updateCustomerLoading || isLoading || loading}
            onClick={handleSubmit}
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Customers;
